import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Bell } from 'lucide-react';
import api from '../services/api';
import { useAuth } from './AuthContext';
import './ServerNotificationContext.css';

const ServerNotificationContext = createContext();

export const useServerNotifications = () => {
  const context = useContext(ServerNotificationContext);
  if (!context) {
    throw new Error('useServerNotifications must be used within a ServerNotificationProvider');
  }
  return context;
};

export const ServerNotificationProvider = ({ children }) => {
  const { user, isAdmin } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [countsByType, setCountsByType] = useState({});
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // Sync pending requests with notifications (for admin/head users)
  const syncPendingRequests = useCallback(async () => {
    if (!user || (user.role?.toLowerCase() !== 'admin' && user.role?.toLowerCase() !== 'manager' && user.role?.toLowerCase() !== 'head')) {
      return;
    }
    
    try {
      await api.post('/notifications/sync-pending');
    } catch (error) {
      console.error('Error syncing pending requests:', error);
    }
  }, [user]);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      // First sync pending requests for admin/head users
      // Temporarily disabled due to backend issues
      // await syncPendingRequests();
      
      const [notifs, countData, typeCounts] = await Promise.all([
        api.get('/notifications/unread'),
        api.get('/notifications/count'),
        api.get('/notifications/counts-by-type'),
      ]);
      
      setNotifications(notifs || []);
      setUnreadCount(countData?.count || 0);
      setCountsByType(typeCounts?.counts || {});
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [user, syncPendingRequests]);

  // Mark notification as read
  const markAsRead = useCallback(async (id) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setNotifications(prev => prev.filter(n => n.id !== id));
      setUnreadCount(prev => Math.max(0, prev - 1));
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, [fetchNotifications]);

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    try {
      await api.patch('/notifications/read-all');
      setNotifications([]);
      setUnreadCount(0);
      setCountsByType({});
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  }, []);

  // Mark notifications of specific entity types as read
  const markAsReadByEntityType = useCallback(async (types) => {
    if (!types || !types.length) return;
    try {
      await api.patch('/notifications/read-by-type', { types });
      // Refresh counts
      const [notifs, countData, typeCounts] = await Promise.all([
        api.get('/notifications/unread'),
        api.get('/notifications/count'),
        api.get('/notifications/counts-by-type'),
      ]);
      setNotifications(notifs || []);
      setUnreadCount(countData?.count || 0);
      setCountsByType(typeCounts?.counts || {});
    } catch (error) {
      console.error('Error marking notifications by type as read:', error);
    }
  }, []);

  // Delete all notifications
  const deleteAllNotifications = useCallback(async () => {
    try {
      await api.delete('/notifications/delete-all');
      setNotifications([]);
      setUnreadCount(0);
      setCountsByType({});
    } catch (error) {
      console.error('Error deleting all notifications:', error);
    }
  }, []);

  // Get notification count for a specific page/entity type
  const getNotificationCountForType = useCallback((type) => {
    return countsByType[type] || 0;
  }, [countsByType]);

  // Check if a page has notifications
  const hasNotificationsForPage = useCallback((pageType) => {
    return (countsByType[pageType] || 0) > 0;
  }, [countsByType]);

  // Poll for new notifications every 30 seconds
  useEffect(() => {
    if (!user) return;
    
    fetchNotifications();
    
    const interval = setInterval(() => {
      fetchNotifications();
    }, 30000);

    return () => clearInterval(interval);
  }, [user, fetchNotifications]);

  const value = {
    notifications,
    unreadCount,
    countsByType,
    loading,
    showDropdown,
    setShowDropdown,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    markAsReadByEntityType,
    deleteAllNotifications,
    getNotificationCountForType,
    hasNotificationsForPage,
  };

  return (
    <ServerNotificationContext.Provider value={value}>
      {children}
    </ServerNotificationContext.Provider>
  );
};

// Notification Bell Component with Badge
export const NotificationBell = () => {
  const { unreadCount, showDropdown, setShowDropdown, notifications, markAsRead, markAllAsRead, deleteAllNotifications } = useServerNotifications();

  return (
    <div className="notification-bell-container">
      <button 
        className="notification-bell-btn"
        onClick={() => setShowDropdown(!showDropdown)}
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>
      
      {showDropdown && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <h4>Notifications</h4>
            {notifications.length > 0 && (
              <button onClick={markAllAsRead} className="mark-all-read">
                Mark all read
              </button>
            )}
          </div>
          
          <div className="notification-list">
            {notifications.length === 0 ? (
              <p className="no-notifications">No new notifications</p>
            ) : (
              notifications.map(notification => (
                <div key={notification.id} className="notification-item">
                  <div className="notification-content">
                    <p className="notification-title">{notification.title}</p>
                    <p className="notification-message">{notification.message}</p>
                    <span className="notification-time">
                      {new Date(notification.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <button 
                    className="mark-read-btn"
                    onClick={() => markAsRead(notification.id)}
                  >
                    ✓
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ServerNotificationContext;
